import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, GET_ELECTRONIC_DOCS, roles, RootState } from "shared";
import { getSearchHistoryAutorizationPayments } from "shared/utils";
import { addYears } from "date-fns";
import { DatePickerConfig } from "../types/DatePickerConfig.type";
import { useAuth } from "shared/auth";
import { buildDatePickerRange } from "shared/utils/helpers/buildDatePickerRange";

export const useHookFormAutorization = (
  hasContract: boolean,
  setShowForm?: (value: boolean) => void,
  setAppliedFilters?: (filters: any) => void,
) => {
  const dispatch = useDispatch<Dispatch>();
  const { paramsUrl } = useSelector(
    (state: RootState) => state.electronicDocuments,
  );
  const { paymentsStates, users } = useSelector(
    (state: RootState) => state.optionsDropdowns,
  );
  const invoiceStates = useSelector((state: RootState) => state.invoiceStates);
  const { authService } = useAuth();
  const inforUser = authService.getUser();
  const userRoles = authService.getUserRoles();
  const [invoiceDateServiceAt, setInvoiceDateServiceAt] = useState<any[]>([
    null,
    null,
  ]);
  const [startInvoiceDateServiceAt, endInvoiceDateServiceAt] =
    invoiceDateServiceAt;
  const [radicadoAt, setRadicadoAt] = useState<any[]>([null, null]);
  const [startDateRadicadoAt, endDateRadicadoAt] = radicadoAt;
  const [userId, setUserId] = useState("");
  const [openModalStates, setOpenModalStates] = useState(false);
  const [statesSelected, setStatesSelected] = useState<any[]>([]);
  const [inputFieldState, setInputFieldState] = useState<{
    isReadOnly: boolean;
    defaultValue: string;
  }>({
    isReadOnly: false,
    defaultValue: "",
  });
  const dataLocalStorageAutorizationPayments = useMemo(
    () => getSearchHistoryAutorizationPayments(),
    [],
  );
  const validateActivitiesSelected = useMemo(
    () => () =>
      invoiceStates.some((value) => value.activities.some((x) => x.selected)),
    [invoiceStates],
  );

  useEffect(() => {
    validateAuditorField();
    dayjs.extend(utc);
  }, []);

  useEffect(() => {
    const paymentStates = paymentsStates.map((state) => ({
      ...state,
      selected: false,
    }));
    setStatesSelected(paymentStates);
  }, []);

  const onCloseModal = () => setOpenModalStates(false);

  const selectAllActivies = () => {
    const allIsSelected = statesSelected.every((state) => state.selected);
    const currentState = statesSelected.map((st) => ({
      ...st,
      selected: !allIsSelected,
    }));
    setStatesSelected(currentState);
  };

  const onSelected = (id: number) => {
    const currentState = statesSelected.map((st) => ({
      ...st,
      selected: st.id === id ? !st.selected : st.selected,
    }));
    setStatesSelected(currentState);
  };

  const buildUrl = (valuesUrl: any) => {
    const activitiesFilter = invoiceStates
      .flatMap((value) => value.activities.filter((x) => x.selected))
      .map((activity) => ({
        states: activity.titulo,
      }));
    const queryString = activitiesFilter
      .map((item) => `states=${item.states}`)
      .join("&");
    const url = `${GET_ELECTRONIC_DOCS}/page_payments/?${queryString}`;
    const params = {
      ...paramsUrl,
      userId: valuesUrl.authorizationValue?.id || "",
      paymentLineId: valuesUrl.paymentLine,
      stateInvoice: valuesUrl.stateInvoice,
      attentionPeriodStartedAt: startInvoiceDateServiceAt
        ? dayjs.utc(startInvoiceDateServiceAt).format("YYYY-MM-DD")
        : "",
      attentionPeriodEndedAt: endInvoiceDateServiceAt
        ? dayjs.utc(endInvoiceDateServiceAt).format("YYYY-MM-DD")
        : "",
      documentNumber: valuesUrl.invoiceReference,
      employerIdNumber: valuesUrl.nitEntidad,
      filingNumber: valuesUrl.radicado,
      filingNumberCreatedAtStart: startDateRadicadoAt
        ? dayjs.utc(startDateRadicadoAt).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        : "",
      filingNumberCreatedAtEnd: endDateRadicadoAt
        ? dayjs.utc(endDateRadicadoAt).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        : "",
      isCertified: "false",
      states: activitiesFilter.map((item) => item.states).join(","),
      isContract: hasContract,
      orderBy: "1",
      size: 50,
    };
    return { params, url };
  };

  const formik = useFormik({
    initialValues: {
      paymentLine:
        dataLocalStorageAutorizationPayments?.params?.paymentLine ?? "",
      stateInvoice:
        dataLocalStorageAutorizationPayments?.params?.stateInvoice ?? "",
      invoiceReference:
        dataLocalStorageAutorizationPayments?.params?.documentNumber ?? "",
      nitEntidad:
        dataLocalStorageAutorizationPayments?.params?.employerIdNumber ?? "",
      radicado:
        dataLocalStorageAutorizationPayments?.params?.filingNumber ?? "",
      userName: dataLocalStorageAutorizationPayments?.params?.userName ?? "",
      authorizationValue: null,
      withCertificate:
        dataLocalStorageAutorizationPayments?.params?.withCertificate ?? true,
    },
    onSubmit: (vls) => {
      const result = buildUrl(vls);
      // saveSearchHistoryAutorizationPayments(result)
      // Update applied filters instead of calling getElectronicDocuments
      setAppliedFilters && setAppliedFilters(result.params);
      setShowForm && setShowForm(false);
    },
    validateOnChange: false,
    enableReinitialize: true,
  });

  const { handleChange, handleSubmit, values, setFieldValue } = formik;

  const getDatePickerConfig = (): DatePickerConfig => ({
    invoiceDateService: buildDatePickerRange(
      startInvoiceDateServiceAt,
      endInvoiceDateServiceAt,
    ),
    radicadoAt: buildDatePickerRange(startDateRadicadoAt, endDateRadicadoAt),
  });

  const getValueDatePickerStart = (name: keyof DatePickerConfig): Date | null =>
    getDatePickerConfig()[name]!.start();

  const getValueDatePickerEnd = (name: keyof DatePickerConfig): Date | null =>
    getDatePickerConfig()[name]!.end();

  const maxDate = (name: keyof DatePickerConfig): Date =>
    getDatePickerConfig()[name]!.max();

  const handleUserAssignment = (value: any) => {
    const user = value?.id ?? "";
    setUserId(user);
    setFieldValue("authorizationValue", value);
  };

  const onChangesDatePicker = (update: any, name: string) => {
    if (name === "invoiceDateService") setInvoiceDateServiceAt(update);
    if (name === "radicadoAt") setRadicadoAt(update);
  };

  const validateAuditorField = () => {
    const userRolIsAuditor = userRoles.some((role) => role === roles.auditor);

    users.forEach((user) => {
      if (String(user.id) === inforUser?.oid && userRolIsAuditor) {
        setInputFieldState({ isReadOnly: true, defaultValue: user.name });
      }
    });
  };

  const handleClear = () => {
    // Reset form values
    formik.resetForm();
    // Reset date pickers
    setInvoiceDateServiceAt([null, null]);
    setRadicadoAt([null, null]);
    // Reset user assignment
    setUserId("");
    // Reset states selected
    setStatesSelected([]);
    // Reset states selected in Redux
    dispatch.invoiceStates.resetStatesSelected();
    // Clear applied filters
    setAppliedFilters && setAppliedFilters({});
    // Close form
    setShowForm && setShowForm(false);
  };

  return {
    handleSubmit,
    handleChange,
    getValueDatePickerStart,
    getValueDatePickerEnd,
    maxDate,
    handleUserAssignment,
    onCloseModal,
    openModalStates,
    statesSelected,
    inputFieldState,
    values,
    selectAllActivies,
    onSelected,
    onChangesDatePicker,
    validateActivitiesSelected,
    handleClear,
  };
};
